'use client';

import { forwardRef, useState, useId, ReactNode, ChangeEvent, FocusEvent } from 'react';
import {
  Box,
  Field,
  IconButton,
  Text,
  HTMLChakraProps,
} from '@chakra-ui/react';
import { InputField } from './input';
import { TextareaField } from './textarea';
import SelectInput from './selectInput';
import { HiEye, HiEyeSlash } from 'react-icons/hi2';

export interface FormFieldOption {
  label: string;
  value: string;
}

export type FormFieldType =
  | 'text'
  | 'password'
  | 'email'
  | 'number'
  | 'tel'
  | 'url'
  | 'search'
  | 'date'
  | 'time'
  | 'datetime-local'
  | 'select'
  | 'textarea';

export interface FormFieldProps extends Omit<HTMLChakraProps<'div'>, 'onChange' | 'onBlur' | 'onFocus'> {
  name?: string;
  label?: string;
  required?: boolean;
  type?: FormFieldType | string;
  value?: any;
  defaultValue?: any;
  onChange?: (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | string | any) => void;
  onBlur?: (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onFocus?: (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  placeholder?: string;
  error?: string | null;
  helpText?: string;
  help?: string;
  disabled?: boolean;
  readOnly?: boolean;
  autoFocus?: boolean;
  autoComplete?: string;
  spellCheck?: boolean;
  maxLength?: number;
  rightIcon?: ReactNode;
  showPasswordToggle?: boolean;
  showPasswordLabel?: string;
  hidePasswordLabel?: string;
  e2eTitle?: string;
  'data-e2e'?: string;
  options?: FormFieldOption[];
  css?: any;
}

/**
 * Canonical labelled form field composite for `@themes/aegix`.
 *
 * Encapsulates `Field.Root`, `Field.Label` (with error styling),
 * input control (`InputField`, `SelectInput`, or `TextareaField`), password visibility toggle,
 * right icon, `e2eTitle` mapping to `data-e2e`, and a reserved `minH="20px"` error/help slot
 * with `role="alert"`.
 */
export const FormField = forwardRef<HTMLInputElement | HTMLTextAreaElement | HTMLDivElement, FormFieldProps>(
  function FormField(props, ref) {
    const {
      name,
      label,
      required,
      type = 'text',
      value,
      defaultValue,
      onChange,
      onBlur,
      onFocus,
      placeholder,
      error,
      helpText,
      help,
      disabled,
      readOnly,
      autoFocus = false,
      autoComplete,
      spellCheck = false,
      maxLength,
      rightIcon,
      showPasswordToggle = false,
      showPasswordLabel = 'Show password',
      hidePasswordLabel = 'Hide password',
      e2eTitle,
      'data-e2e': dataE2EProp,
      options,
      css,
      ...restProps
    } = props;

    const [showPassword, setShowPassword] = useState(false);
    const autoId = useId();
    const fieldName = name || `field-${autoId}`;
    const fieldId = name || fieldName;
    const dataE2E = dataE2EProp || e2eTitle;
    const errorId = `${fieldId}-error`;
    const helpId = `${fieldId}-help`;
    const helpMessage = helpText || help;
    const isInvalid = Boolean(error);
    const ariaDescribedBy = isInvalid ? errorId : helpMessage ? helpId : (props as any)['aria-describedby'];

    const handleTextChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | any) => {
      if (!onChange) return;
      onChange(e);
    };

    if (type === 'select') {
      return (
        <Box width="100%" {...restProps}>
          <SelectInput
            id={fieldId}
            name={fieldName}
            label={label}
            option={options ?? []}
            value={value}
            defaultValue={defaultValue}
            onChange={(val: string) => {
              if (onChange) onChange(val);
            }}
            disabled={disabled}
            readOnly={readOnly}
            required={required}
            invalid={isInvalid}
            e2eTitle={dataE2E}
            placeholder={placeholder}
            aria-describedby={ariaDescribedBy}
          />
          <Box minH="20px" mt={1}>
            {error && (
              <Text
                id={errorId}
                role="alert"
                data-e2e="inputError"
                color="text.danger"
                fontSize="xs"
              >
                {error}
              </Text>
            )}
            {helpMessage && !error && (
              <Text id={helpId} fontSize="xs" color="text.tertiary">
                {helpMessage}
              </Text>
            )}
          </Box>
        </Box>
      );
    }

    const commonInputProps: Record<string, any> = {
      id: fieldId,
      name: fieldName,
      value,
      defaultValue,
      onChange: handleTextChange,
      onBlur: onBlur as any,
      onFocus: onFocus as any,
      placeholder,
      disabled,
      readOnly,
      autoFocus,
      maxLength,
      'data-e2e': dataE2E,
      borderWidth: error ? '2px' : '1px',
      borderColor: error ? 'border.danger' : undefined,
      'aria-invalid': isInvalid || undefined,
      css,
    };

    if (ariaDescribedBy) {
      commonInputProps['aria-describedby'] = ariaDescribedBy;
    }

    if (type === 'textarea') {
      return (
        <Field.Root
          width="100%"
          position="relative"
          textAlign="start"
          invalid={isInvalid}
          required={required}
          disabled={disabled}
          readOnly={readOnly}
          {...restProps}
        >
          {label && (
            <Field.Label
              color={error ? 'text.danger' : 'text.primary'}
              textTransform="capitalize"
              htmlFor={fieldId}
              fontWeight={600}
              pr={2}
              fontSize={{ base: 'sm', md: 'md' }}
            >
              {label}
            </Field.Label>
          )}
          <TextareaField ref={ref as any} {...commonInputProps} />
          <Box minH="20px" mt={1}>
            {error && (
              <Text
                id={errorId}
                role="alert"
                data-e2e="inputError"
                color="text.danger"
                fontSize="xs"
              >
                {error}
              </Text>
            )}
            {helpMessage && !error && (
              <Text id={helpId} fontSize="xs" color="text.tertiary">
                {helpMessage}
              </Text>
            )}
          </Box>
        </Field.Root>
      );
    }

    const effectiveType = type === 'password' ? (showPassword ? 'text' : 'password') : type;

    return (
      <Field.Root
        width="100%"
        position="relative"
        textAlign="start"
        invalid={isInvalid}
        required={required}
        disabled={disabled}
        readOnly={readOnly}
        {...restProps}
      >
        {label && (
          <Field.Label
            color={error ? 'text.danger' : 'text.primary'}
            textTransform="capitalize"
            htmlFor={fieldId}
            fontWeight={600}
            pr={2}
            fontSize={{ base: 'sm', md: 'md' }}
          >
            {label}
          </Field.Label>
        )}
        <Box position="relative" width="100%">
          <InputField
            ref={ref as any}
            type={effectiveType as any}
            autoComplete={autoComplete}
            spellCheck={spellCheck}
            pr={type === 'password' && showPasswordToggle ? '40px' : rightIcon ? '40px' : undefined}
            {...commonInputProps}
          />
          {type === 'password' && showPasswordToggle && (
            <Box
              position="absolute"
              right="8px"
              top="50%"
              transform="translateY(-50%)"
              zIndex="1"
            >
              <IconButton
                aria-label={showPassword ? hidePasswordLabel : showPasswordLabel}
                variant="ghost"
                size="xs"
                onClick={() => setShowPassword((prev) => !prev)}
                type="button"
                data-e2e="togglePasswordVisibility"
              >
                {showPassword ? <HiEyeSlash /> : <HiEye />}
              </IconButton>
            </Box>
          )}
          {rightIcon && type !== 'password' && (
            <Box
              position="absolute"
              right="8px"
              top="50%"
              transform="translateY(-50%)"
              zIndex="1"
            >
              {rightIcon}
            </Box>
          )}
        </Box>
        <Box minH="20px" mt={1}>
          {error && (
            <Text
              id={errorId}
              role="alert"
              data-e2e="inputError"
              color="text.danger"
              fontSize="xs"
            >
              {error}
            </Text>
          )}
          {helpMessage && !error && (
            <Text id={helpId} fontSize="xs" color="text.tertiary">
              {helpMessage}
            </Text>
          )}
        </Box>
      </Field.Root>
    );
  }
);
